import { useEffect, useState } from 'react';
import { Flame } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2K PR 3 — Comeback Callout.
 *
 * Renders the persisted comeback narrative ("Comeback of the Split") as a
 * cinematic banner. Rare-by-design — only mounts when the backend has
 * detected a triple-gated comeback.
 *
 * Two modes:
 *   <ArenaxComebackCallout tournamentId="…" />  → fetch + render one story
 *   <ArenaxComebackCallout seasonId="…" />      → list of stories (Eternal League)
 *
 * Tone: legendary, cinematic, never analytical. See narrative_style_guide.md.
 */
export default function ArenaxComebackCallout({ tournamentId, seasonId, variant = 'banner' }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let url;
    if (tournamentId) {
      url = `/arenax/tournaments/${tournamentId}/comeback`;
    } else if (seasonId) {
      url = `/arenax/seasons/${seasonId}/comebacks`;
    } else {
      setLoading(false);
      return;
    }
    api.get(url)
      .then(r => {
        const data = r.data || {};
        if (tournamentId) {
          setStories(data.story ? [data.story] : []);
        } else {
          setStories(data.stories || []);
        }
      })
      .catch(() => setStories([]))
      .finally(() => setLoading(false));
  }, [tournamentId, seasonId]);

  if (loading || stories.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="arenax-comeback-callouts">
      {stories.map((s, idx) => (
        <ComebackBanner key={s.id || idx} story={s} variant={variant} />
      ))}
    </div>
  );
}

function ComebackBanner({ story, variant }) {
  return (
    <div
      data-testid={`arenax-comeback-callout-${story.tournament_id}`}
      className="relative rounded-2xl border border-amber-500/35 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(251,113,133,0.12), rgba(245,158,11,0.10) 50%, var(--ax-card-bg-soft))',
      }}>
      {/* Subtle radial flare in the corner */}
      <div className="pointer-events-none absolute -top-12 -right-12 w-48 h-48 rounded-full"
        style={{ background: 'radial-gradient(closest-side, rgba(251,113,133,0.25), transparent 70%)' }} />
      <div className="relative p-5">
        <div className="flex items-center gap-2 mb-2.5">
          <Flame className="h-4 w-4 text-amber-300 animate-pulse" />
          <span className="text-[10px] tracking-[0.3em] text-amber-300 font-black uppercase">
            Comeback of the Split
          </span>
        </div>
        <p className="text-sm sm:text-base text-slate-100 font-bold leading-snug"
          data-testid={`arenax-comeback-story-${story.tournament_id}`}>
          {story.story}
        </p>
        {(story.season_label || story.tournament_name) && (
          <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            {story.season_label || story.tournament_name}
          </div>
        )}
      </div>
    </div>
  );
}
